<template>
  <Modal position="end" ref="modalDialogRef" @after-close="emit('closed')">
    <template #header>Access instructions</template>

    <template #body>
      <View gap="lg" direction="column">
        <View direction="column" gap="sm">
          <Text variant="h3">{{ isEditMode ? 'Edit access instructions' : 'Set up access instructions' }}</Text>
          <Text variant="body-medium" color="tertiary">
            Renters get this when they confirm a self-guided showing at this property.
          </Text>
        </View>

        <SelfGuidedAccessForm ref="accessFormRef" v-model="method" :property-id="propertyId" />

        <Card v-if="!isCodebox && method" variant="secondary" class="full-width">
          <View direction="column" gap="sm">
            <Text variant="label-medium">Expiration date</Text>
            <Input type="date" v-model="expiresAt" :min="minDateString" data-testid="access-expires-at" />
          </View>
        </Card>
      </View>
    </template>

    <template #footer>
      <Button
        v-if="isEditMode && !isCodebox"
        variant="ghost"
        appearance="danger"
        @click="handleDelete"
        :loading="isDeleting"
      >
        Delete
      </Button>
      <View gap="md">
        <Button variant="ghost" @click="close">Cancel</Button>
        <Button :disabled="isSubmitDisabled" :loading="isSaving" @click="handleSave" data-testid="access-modal-save">
          Save
        </Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { Button, Card, Input, Modal, Text, useAlertsStore, View } from '@tallo/design-system';
import {
  PropertyAccessInstructionDto,
  SelfGuidedAccessForm,
  useDeleteAccessInstructions,
  usePropertyStore,
} from '@tallo/investor/property';
import { SelfGuidedAccessMethod } from '@tallo/property';

import { getMinDateString } from '../utils/date.utils';

const emit = defineEmits(['closed']);

const props = defineProps<{
  instruction?: PropertyAccessInstructionDto | null;
  propertyId: string;
}>();

const modalDialogRef = ref<InstanceType<typeof Modal> | null>(null);
const accessFormRef = ref<InstanceType<typeof SelfGuidedAccessForm> | null>(null);

const propertyStore = usePropertyStore();
const deleteMutation = useDeleteAccessInstructions();

const method = ref<SelfGuidedAccessMethod | null>(propertyStore.property?.selfGuidedAccessMethod ?? null);
const isCodebox = computed(() => method.value === SelfGuidedAccessMethod.CODEBOX);

const expiresAt = ref(toDateInput(props.instruction?.expiresAt));
const minDateString = getMinDateString();

const isEditMode = computed(() => !!props.instruction);
const isSaving = ref(false);
const isDeleting = computed(() => deleteMutation.isLoading.value);

const isSubmitDisabled = computed(() => isSaving.value || !accessFormRef.value?.isValid);

function toDateInput(value?: string | null): string {
  return value ? new Date(value).toISOString().split('T')[0] : '';
}

watch(
  () => props.instruction,
  (instruction) => (expiresAt.value = toDateInput(instruction?.expiresAt)),
);

watch(
  () => propertyStore.property?.selfGuidedAccessMethod,
  (saved) => (method.value = saved ?? null),
);

async function handleSave() {
  isSaving.value = true;

  try {
    if (method.value !== propertyStore.property?.selfGuidedAccessMethod) {
      await propertyStore.saveAccessMethod(method.value);
    }

    await accessFormRef.value?.save(expiresAt.value || undefined);

    useAlertsStore().success('Access instructions updated');
    close();
  } finally {
    isSaving.value = false;
  }
}

async function handleDelete() {
  await deleteMutation.mutateAsync({
    propertyId: props.propertyId,
    instructionId: props.instruction!.id,
  });

  close();
}

function open() {
  modalDialogRef.value?.show();
}

function close() {
  modalDialogRef.value?.close();
}

defineExpose({
  open,
  close,
});
</script>

<style scoped lang="scss">
.full-width {
  width: 100%;
}
</style>
